import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CURRENT_REVISION, REVISIONS, DOC, RMA_DOC, RMA_REVISIONS, RMA_CURRENT_REVISION, LOOPS } from './supportFlowData';
import { PLAN_DOC } from './supportRolloutData';

const currentFlowRev = REVISIONS.find(r => r.rev === CURRENT_REVISION) || REVISIONS[REVISIONS.length - 1];
const currentRmaRev = RMA_REVISIONS.find(r => r.rev === RMA_CURRENT_REVISION) || RMA_REVISIONS[RMA_REVISIONS.length - 1];

const TOOLS = [
  {
    id: 'flow',
    to: '/support/flow',
    eyebrow: `${DOC.id} + ${RMA_DOC.id}`,
    title: 'Support loop & RMA intake',
    lead: `One process across two systems. Flow A is the support loop and runs in ${LOOPS.support.system}; Flow B is the service loop and runs in ${LOOPS.service.system}. The seam between them — where a unit starts coming back — is drawn explicitly.`,
    points: [
      'Click any block for what it is, who owns it, and what it must produce',
      'Both flows are revision-controlled and switch independently',
      'Flow B Rev 02 is read from the code — real statuses, real endpoints, nine real emails',
      'A drawn-vs-built comparison lists ten places the old flowchart and the system disagree',
    ],
    status: `A ${currentFlowRev.label} · B ${currentRmaRev.label}`,
    statusTone: currentRmaRev.status === 'As built' ? 'green' : 'amber',
  },
  {
    id: 'start-rma',
    to: '/support/start-rma',
    eyebrow: 'Handover',
    title: 'Start an RMA',
    lead: 'The door from the support loop into the service loop. Paste the service tags you have in front of you and open the RMA form on the right unit.',
    points: [
      'Resolves several tags at once — the public lookup is rate-limited and cannot',
      'Shows warranty position and warns if a unit already has a live case',
      'Opens the real RMA form on the device page, prefilled',
      'Explains what submitting actually does, including when the MTTR clock starts',
    ],
    status: 'Live tool',
    statusTone: 'green',
  },
  {
    id: 'rollout',
    to: '/support/rollout',
    eyebrow: PLAN_DOC.id,
    title: 'Internal rollout & communication plan',
    lead: 'How the Support system becomes the defined way of working: what changes, who must use it, where communication belongs, and how we will know it worked.',
    points: [
      'Objective → Audience → Key messages → Roles → Channels → Timeline → Training → Feedback',
      'Explicit scope boundary, and what stops as well as what starts',
      'Nine measurable success criteria and a rollout risk register',
      'Ready-to-send announcement, manager talking points, Teams post and desk card',
    ],
    status: `${PLAN_DOC.revision} · ${PLAN_DOC.status}`,
    statusTone: 'amber',
  },
];

const TONE = {
  green: { color: 'var(--ni-green)', border: 'rgba(70,187,137,0.5)', bg: 'rgba(70,187,137,0.1)' },
  amber: { color: '#E8CD7E', border: 'rgba(217,180,74,0.5)', bg: 'rgba(217,180,74,0.1)' },
};

export default function SupportToolsHub() {
  const navigate = useNavigate();

  return (
    <div className="ni-page">
      <div className="ni-shell">
        <header className="ni-header">
          <div className="ni-brand">
            <img src="/north-invent-logo.svg" alt="North Invent" className="ni-brand-logo" />
            <div className="ni-brand-copy">
              <span className="ni-eyebrow">North Invent Service</span>
              <span className="ni-brand-title">Support Tools</span>
              <span className="ni-brand-subtitle">How support works, and how we roll it out</span>
            </div>
          </div>
          <div className="ni-toolbar">
            <button className="ni-button ni-button--ghost" onClick={() => navigate('/')}>← Back to Main</button>
          </div>
        </header>

        <section className="ni-panel">
          <p style={{ margin: 0, fontSize: 15, lineHeight: 1.7, color: 'var(--ni-text-soft)' }}>
            Two documents that belong together. The <strong style={{ color: 'var(--ni-text)' }}>flow</strong> defines what
            the support process is. The <strong style={{ color: 'var(--ni-text)' }}>rollout plan</strong> defines how it
            becomes the way people actually work. Both are revision-controlled, because a process nobody can point at a
            version of is a process nobody is accountable to.
          </p>
        </section>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 16 }}>
          {TOOLS.map(tool => {
            const t = TONE[tool.statusTone] || TONE.green;
            return (
              <button
                key={tool.id}
                type="button"
                onClick={() => navigate(tool.to)}
                className="ni-panel"
                style={{
                  textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'var(--ni-text)',
                  display: 'flex', flexDirection: 'column', gap: 10, transition: 'border-color 0.15s, transform 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = 'rgba(70,187,137,0.45)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--ni-border-soft)'; e.currentTarget.style.transform = 'translateY(0)'; }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 10, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--ni-text-muted)' }}>
                    {tool.eyebrow}
                  </span>
                  <span style={{
                    fontSize: 9.5, fontWeight: 800, letterSpacing: '0.11em', textTransform: 'uppercase',
                    color: t.color, border: `1px solid ${t.border}`, background: t.bg,
                    borderRadius: 999, padding: '2px 9px', whiteSpace: 'nowrap',
                  }}>{tool.status}</span>
                </div>
                <h2 style={{ margin: 0, fontSize: 21, fontWeight: 700, lineHeight: 1.2 }}>{tool.title}</h2>
                <p style={{ margin: 0, fontSize: 13.5, lineHeight: 1.65, color: 'var(--ni-text-muted)' }}>{tool.lead}</p>
                <ul style={{ margin: '2px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.65, color: 'var(--ni-text-soft)' }}>
                  {tool.points.map((p, i) => <li key={i} style={{ marginBottom: 3 }}>{p}</li>)}
                </ul>
                <span style={{ marginTop: 'auto', paddingTop: 8, fontSize: 12.5, fontWeight: 700, color: 'var(--ni-green)' }}>
                  Open →
                </span>
              </button>
            );
          })}
        </div>

        <section className="ni-panel">
          <h2 className="ni-section-title" style={{ marginTop: 0, marginBottom: 4 }}>Where this sits</h2>
          <p className="ni-section-copy" style={{ marginTop: 0, marginBottom: 12 }}>
            Support is the front end of work these tools already handle. The flow hands over to them by design, and both
            directions of the handover are part of the process.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {[
              ['/rmas', 'RMA Dashboard', 'Where #5.2 hands over'],
              ['/complaints', 'Supplier NCR', 'Where #5.5 findings land'],
              ['/quality', 'Product QMS', 'Documents, NCR and corrective action'],
              ['/iceberg', 'Iceberg Model', 'The cost of what support sees'],
              ['/current-stats', 'Current Stats', 'The production side of < 2/1000'],
              ['/service/training', 'Training Library', 'Evidence images from real cases'],
            ].map(([to, label, note]) => (
              <button
                key={to}
                type="button"
                onClick={() => navigate(to)}
                style={{
                  font: 'inherit', cursor: 'pointer', textAlign: 'left',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid var(--ni-border-soft)',
                  borderRadius: 12, padding: '9px 13px', color: 'var(--ni-text)',
                }}
              >
                <div style={{ fontSize: 12.5, fontWeight: 700 }}>{label}</div>
                <div style={{ fontSize: 11, color: 'var(--ni-text-muted)' }}>{note}</div>
              </button>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
